import { ComponentClass } from "react"
import type { IContext } from ".."
import { LayoutProps } from "../layout"
import { IBundle } from "../models/bundle"
import { AssetConfig } from "../models/configuration"
import { Dictionary } from "../models/dictionary"

export type AssetLoadProgressHandler = (progress: Array<AssetLoadProgress>, totalCount?: number) => void

export type LoadProgress = {
    url: string
    total: number
    loaded: number
    ready?: boolean
}

export type AssetType = "BUNDLE" | "LAYOUT" | "TRANSITIONS"

export type AssetLoadProgress = LoadProgress & {
    type: AssetType
    name: string
}

export function loadAssets(context: IContext, onProgress?: AssetLoadProgressHandler) {
    const promises: Array<Promise<void>> = []
    context.assetLoadCount = 0

    // Load Layouts
    if (context.config.layouts && Object.keys(context.config.layouts).length) {
        const layoutConfigs = Object.keys(context.config.layouts)
            .filter((key) => !context.layouts[key])
            .map((key) => ({ key, config: context.config.layouts[key] }))
        const promise = loadTypedAssets<ComponentClass<LayoutProps>>(layoutConfigs, "LAYOUT", context, onProgress).then((assets) => {
            const layouts: Dictionary<ComponentClass<LayoutProps>> = {}
            Object.keys(assets).map((key) => {
                const { asset } = assets[key]
                if (!asset) {
                    return
                }
                layouts[key] = asset
            })
            context.layouts = {
                ...context.layouts,
                ...layouts,
            }
        })
        promises.push(promise)
        context.assetLoadCount += layoutConfigs.length
    }

    // Load bundlePromises
    Object.entries(context.bundlePromises).forEach(([key, bundlePromise]) => {
        const promise = new Promise<void>((resolve) => {
            bundlePromise.then(({ default: bundle }) => {
                context.bundles = {
                    ...context.bundles,
                    [key]: bundle,
                }
                const assetProgress: AssetLoadProgress = {
                    type: "BUNDLE",
                    name: key,
                    loaded: 0,
                    total: 0,
                    url: "",
                    ready: true,
                }
                addAssetLoadProgress(assetProgress, context)
                onProgress && onProgress(context.assetLoadProgresses, context.assetLoadCount)
                resolve()
            })
        })
        promises.push(promise)
        context.assetLoadCount++
    })

    // Load bundles
    if (false && context.config.bundles && Object.keys(context.config.bundles).length) {
        const bundleConfigs = Object.keys(context.config.bundles)
            .filter((key) => !context.bundles[key])
            .map((key) => ({ key, config: context.config.bundles[key] }))
        const promise = loadTypedAssets<IBundle>(bundleConfigs, "BUNDLE", context, onProgress).then((assets) => {
            const bundles: Dictionary<IBundle> = {}
            Object.keys(assets).map((key) => {
                const { config, asset } = assets[key]
                if (!asset) {
                    return
                }

                // replace ~ in styles url to the url from which the bundle was loaded
                if (asset.stylesUrl && asset.stylesUrl.indexOf("~") === 0) {
                    let bundleDirectory = ""
                    config.url && (bundleDirectory = config.url.substr(0, config.url.lastIndexOf("/")))
                    asset.stylesUrl = bundleDirectory + asset.stylesUrl.substr(1)
                }
                bundles[key] = asset
            })
            context.bundles = {
                ...context.bundles,
                ...bundles,
            }
        })
        promises.push(promise)
        context.assetLoadCount += bundleConfigs.length
    }

    // Load transitions
    if (context.config.transitions) {
        const handleProgress = (progress: LoadProgress) => {
            const assetProgress: AssetLoadProgress = {
                type: "TRANSITIONS",
                name: "main",
                ...progress,
            }
            addAssetLoadProgress(assetProgress, context)
            onProgress && onProgress(context.assetLoadProgresses)
        }
        const promise = loadScript(context.config.transitions, handleProgress).then((transitions) => {
            context.transitions = [...context.transitions, ...transitions]
        })
        promises.push(promise)
        context.assetLoadCount++
    }

    return Promise.all(promises).then()
}

const jsRegex = /^(.*\/[^\.]*)(?!\.min)(\.js)$/
function loadScript(url: string, onProgress: (progress: LoadProgress) => void) {
    function load(resolve: (value: any) => void, reject: (err: Error) => void, original?: boolean) {
        let urlToLoad = url
        if (!original && process.env.NODE_ENV == "production" && jsRegex.test(url)) {
            urlToLoad = url.replace(jsRegex, "$1.min$2")
        }

        const xhr = new XMLHttpRequest()
        xhr.onreadystatechange = () => {
            if (xhr.readyState == 2) {
                onProgress({
                    url,
                    loaded: 0,
                    total: parseInt(xhr.getResponseHeader("content-length") || "0"),
                })
            }
        }
        xhr.onprogress = (ev: ProgressEvent) =>
            onProgress({
                url,
                loaded: ev.loaded,
                total: ev.total,
            })

        xhr.onerror = () => {
            const msg = `Could not load script ${urlToLoad}`
            if (url != urlToLoad) {
                console.log(`${urlToLoad} not found. Trying to load ${url}`)
                load(resolve, reject, true)
                return
            }
            reject(new Error(msg))
        }

        xhr.onloadend = (ev: ProgressEvent) => {
            onProgress({
                url,
                loaded: ev.loaded,
                total: ev.total,
                ready: true,
            })

            if ((ev.target as any)?.status > 400) {
                if (url != urlToLoad) {
                    console.log(`${urlToLoad} not found. Trying to load ${url}`)
                    load(resolve, reject, true)
                    return
                }
                reject(new Error((ev as any).statusText))
                return
            }

            let script: string = xhr.response || xhr.responseText
            if (!/\/\/# sourceMappingURL=(\/|https?\:\/\/)/.test(script)) {
                script = script.replace(
                    /\/\/# sourceMappingURL=(.*?)\.js\.map/g,
                    `//# sourceMappingURL=${url.substring(0, url.lastIndexOf("/") + 1)}$1.js.map`
                )
            }

            let obj: any
            try {
                obj = eval(script)
            } catch (err) {
                reject(new Error(`Script (${url}) could not be loaded: ${err}`))
                return
            }

            if (!obj) {
                reject(new Error(`No script content loaded (${url})`))
                return
            }

            if (obj.__esModule && obj.default) {
                obj = obj.default
            }

            resolve(obj)
        }

        xhr.open("GET", urlToLoad)
        xhr.send()
    }
    return new Promise<any>(load)
}

function loadTypedAssets<T>(
    configs: Array<{ key: string; config: AssetConfig }>,
    type: AssetType,
    context: IContext,
    onProgress?: AssetLoadProgressHandler
) {
    type AssetResult = { asset: T | false; config: AssetConfig }
    return new Promise<Dictionary<AssetResult>>((resolve, reject) => {
        const proms = configs
            .filter((x) => !!x.config.url)
            .map(
                (c) =>
                    new Promise<{ asset: T | false; key: string; config: AssetConfig }>((resolve, reject) => {
                        const handleProgress = (progress: LoadProgress) => {
                            const assetProgress: AssetLoadProgress = {
                                type,
                                name: c.key,
                                ...progress,
                            }
                            addAssetLoadProgress(assetProgress, context)
                            onProgress && onProgress(context.assetLoadProgresses, context.assetLoadCount)
                        }

                        loadScript(c.config.url as string, handleProgress).then(
                            (asset) => resolve({ asset, key: c.key, config: c.config }),
                            (error) => {
                                console.error(error)
                                resolve({ asset: false, key: c.key, config: c.config })
                            }
                        )
                    })
            )
        Promise.all(proms).then((loadedAssets) => {
            const assets: Dictionary<AssetResult> = {}
            loadedAssets.forEach((item) => (assets[item.key] = item))
            resolve(assets)
        })
    })
}

function addAssetLoadProgress(progress: AssetLoadProgress, context: IContext) {
    const existing = context.assetLoadProgresses.findIndex((x) => x.name == progress.name && x.type == progress.type)
    if (existing != -1) {
        context.assetLoadProgresses = [
            ...context.assetLoadProgresses.slice(0, existing),
            progress,
            ...context.assetLoadProgresses.slice(existing + 1),
        ]
    } else {
        context.assetLoadProgresses.push(progress)
    }
}
